import { z } from "zod/v4";

/**
 * A workspace of user-authored custom code: the editable files (a fixed,
 * domain-chosen entry module plus any helper modules the user adds) keyed by
 * filename -> content. Environment-injected modules (contract types, hidden
 * logger, ...) are provided by the domain's tooling and never stored here.
 */
export interface CodeWorkspace {
  entry: string;
  files: Record<string, string>;
}

/**
 * Filenames the shared bundler generates into the build directory alongside the
 * user files. Reserved for EVERY domain rather than left to per-domain config:
 * they are not domain-shaped (every `AgenticCodegenBundlerService` consumer gets
 * exactly these two), and they are written after the user files, so a workspace
 * file with one of these names would be silently overwritten at bundle time.
 *
 * `agentic-codegen-bundler.service.ts` imports this same constant for its runtime
 * backstop, so the save path and the build path cannot disagree about them. The
 * per-domain `injectedModules` half is genuinely domain-shaped and stays in
 * `reservedFilenames`.
 */
export const GENERATED_BUILD_INPUT_FILENAMES = [
  "entry.ts",
  "package.json",
] as const;

/**
 * Per-domain workspace configuration: the fixed entry filename every
 * workspace of that domain must contain, and the environment-injected
 * filenames users may never author (they would shadow tooling-provided
 * modules).
 */
export interface WorkspaceSchemaConfig<Entry extends string = string> {
  entryFilename: Entry;
  reservedFilenames: readonly string[];
}

/**
 * Filename-safety + entry-presence rules shared by every domain's save-body
 * validator. Every file must be a plain `*.ts` name (no path traversal, no
 * reserved environment module), and the declared `entry` must exist in
 * `files`.
 */
function refineWorkspace(reservedFilenames: readonly string[]) {
  const reservedFilenameSet = new Set<string>(reservedFilenames);

  return (workspace: CodeWorkspace, ctx: z.RefinementCtx): void => {
    const filenames = Object.keys(workspace.files);

    for (const filename of filenames) {
      if (!filename.endsWith(".ts")) {
        ctx.addIssue({
          code: "custom",
          message: `"${filename}" must be a .ts file`,
          path: ["files", filename],
        });
      }
      // Kept character-for-character in step with
      // `assertSafeWorkspaceFilenames` in the shared bundler: a name this
      // accepts but that one rejects saves cleanly and then fails at bundle
      // time with an error written for us and shown to a merchant.
      if (
        filename.includes("/") ||
        filename.includes("\\") ||
        filename.includes("..") ||
        filename.startsWith("/")
      ) {
        ctx.addIssue({
          code: "custom",
          message: `"${filename}" must be a bare filename without path segments`,
          path: ["files", filename],
        });
      }
      if (reservedFilenameSet.has(filename)) {
        ctx.addIssue({
          code: "custom",
          message: `"${filename}" is reserved and cannot be used as a filename`,
          path: ["files", filename],
        });
      }
    }

    if (!filenames.includes(workspace.entry)) {
      ctx.addIssue({
        code: "custom",
        message: `Entry file "${workspace.entry}" must exist in files`,
        path: ["entry"],
      });
    }
  };
}

/**
 * Builds the pair of workspace schemas for one custom-code domain:
 *  - `WorkspaceSchema` — the unrefined base object shape, kept separate so
 *    consumers that need `.omit()`/`.extend()` (which zod-v4 disallows on a
 *    refined schema) can compose it.
 *  - `WorkspaceInputSchema` — the save-path validator: base shape plus the
 *    filename-safety refinement.
 *
 * The return type is deliberately left to inference so each domain's `entry`
 * stays the literal type of its configured entry filename.
 */
export function createWorkspaceSchemas<const Entry extends string>(
  config: WorkspaceSchemaConfig<Entry>
) {
  const WorkspaceSchema = z.object({
    entry: z.literal(config.entryFilename).default(config.entryFilename),
    files: z.record(z.string(), z.string()),
  });

  return {
    WorkspaceSchema,
    WorkspaceInputSchema: WorkspaceSchema.superRefine(
      refineWorkspace([
        ...config.reservedFilenames,
        ...GENERATED_BUILD_INPUT_FILENAMES,
      ])
    ),
  };
}
